import React from 'react';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { FileText, Clock, CreditCard, Activity, ShieldAlert, Phone } from 'lucide-react';

const TermsConditionsPage = () => {
  const sections = [
    {
      icon: <Clock className="text-primary" />,
      title: "Appointment Booking",
      content: "We request appointments to be booked in advance via our website or direct phone call to minimize wait times. Cancellations should be notified at least 2 hours prior to the scheduled time."
    },
    {
      icon: <ShieldAlert className="text-primary" />,
      title: "Emergency Triage",
      content: "Our hospital follows a strict medical triage protocol. Life-threatening emergency cases (trauma, severe bleeding, breathing distress) will always be prioritized over routine consultations, regardless of appointment time."
    },
    {
      icon: <CreditCard className="text-primary" />,
      title: "Payments & Fees",
      content: "All clinical and surgical fees are due at the time the services are rendered. We accept major digital payments, cards, and cash. A deposit may be required for major surgeries or boarding stays."
    },
    {
      icon: <Activity className="text-primary" />,
      title: "Medical Consent",
      content: "By bringing your pet for treatment, you provide consent for basic physical examinations and diagnostic workups. Specific consent forms are required for anesthesia and major surgical procedures."
    }
  ];

  return (
    <>
      <SEO 
        title="Terms & Conditions | Dr. Suresh Pet Care Hospital"
        description="Review our clinical terms and conditions including appointment policies, emergency priorities, and payment for medical services at Dr. Suresh Pet Care."
        canonical="/terms"
      />
      <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="bg-white min-h-screen pt-[140px] md:pt-[160px] pb-24"
    >
      <div className="section-container">
        {/* Header */}
        <div className="max-w-3xl mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/10">
            <FileText size={14} className="text-primary" />
            <span className="text-[10px] font-black tracking-widest uppercase text-primary">Service Agreement</span>
          </div>
          <h1 className="text-navy font-display font-bold text-5xl md:text-7xl leading-tight">
            Terms & <em className="font-normal text-primary not-italic">Conditions.</em>
          </h1>
          <p className="text-muted-foreground text-lg font-medium leading-relaxed">
            By utilizing the clinical and surgical services at Dr. Suresh Pet Care Hospital, you agree to the following professional terms and conditions.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {sections.map((section, i) => (
            <div key={i} className="p-10 bg-secondary/20 rounded-[3rem] border border-navy/5 hover:border-primary/20 transition-all duration-500">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-8">
                {section.icon}
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">{section.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed font-medium">
                {section.content}
              </p>
            </div>
          ))}
        </div>

        {/* Final Disclaimer */}
        <div className="p-12 bg-[#f8faff] rounded-[4rem] text-navy border border-navy/5">
          <div className="max-w-4xl space-y-6">
             <h4 className="text-xl font-bold border-b border-navy/5 pb-4">Medical Responsibility</h4>
             <p className="text-muted-foreground text-sm leading-relaxed">
               While we strive for the best medical outcomes using leading veterinary practices, every clinical case carries inherent risks. Our team will provide thorough counseling and professional care to ensure your pet's safety and wellbeing at every clinical stage.
             </p>
             <div className="flex gap-4 pt-4">
                <a href="tel:+919848954921" className="inline-flex items-center gap-3 bg-navy text-white px-6 py-3 rounded-full font-bold text-xs uppercase tracking-widest">
                  <Phone size={14} /> Call Reception
                </a>
             </div>
          </div>
        </div>
      </div>
    </motion.div>
    </>
  );
};

export default TermsConditionsPage;
